// Kwarachi — cinturones (product.type == 'Belts'): la talla NO llega preseleccionada.
// Mientras el cliente no elija talla, "Agregar al carrito" dice "Elige tu talla", no agrega nada
// y señala el selector; "Comprar ahora" se oculta. Lo marca snippets/variant-main-picker.liquid
// con data-kw-size-required (picker) y data-kw-size-fieldset (opción Talla/Size).
// Si este archivo no carga, la ficha funciona exactamente como antes.
import '@theme/variant-picker';
import { ThemeEvents } from '@theme/events';

const PICKER = 'variant-picker[data-kw-size-required]';
const IN_FLIGHT_MAX_MS = 8000;
const ES = (document.documentElement.lang || '').toLowerCase().startsWith('es');
const TEXT = ES
  ? { choose: 'Elige tu talla', error: 'Elige una talla para continuar.' }
  : { choose: 'Choose your size', error: 'Please choose a size to continue.' };

// El CSS va aquí y no en el snippet: en las colecciones la compra rápida trae el selector sin su hoja de estilos.
const style = document.createElement('style');
style.textContent = `
  [data-kw-size-fieldset]:not(:has(input:checked)) .variant-option__button-label__pill { visibility: hidden; }
  [data-kw-size-fieldset].kw-size--error { outline: 2px solid var(--color-error, #b42318); outline-offset: 6px; border-radius: 8px; }
  .kw-size-error { grid-column: 1 / -1; flex-basis: 100%; width: 100%; margin: 0 0 4px; color: var(--color-error, #b42318); font-size: 0.9rem; font-weight: 600; }
`;
document.head.append(style);

/** @type {WeakMap<Element, {productId: string, valueId: string}>} talla que eligió el cliente, por selector */
const chosen = new WeakMap();
/** @type {WeakMap<Element, number>} cambio de variante pedido al servidor y aún sin respuesta (variant:selected → variant:update) */
const inFlight = new WeakMap();
/** @type {WeakMap<Element, Element>} compra que llegó con el cambio en vuelo; se repite cuando llega la variante */
const deferred = new WeakMap();
let errorCount = 0;

const sizeFieldset = (picker) => picker.querySelector('[data-kw-size-fieldset]');
const scopeOf = (element) => element.closest('dialog, .shopify-section') || document;

/** @param {Element} picker */
function isPending(picker) {
  const fieldset = sizeFieldset(picker);
  // Sin botones de talla no hay nada que elegir: nunca bloquear (falla abierta).
  if (!fieldset) return false;
  const choice = chosen.get(picker);
  if (!choice || choice.productId !== picker.getAttribute('data-product-id')) return true;
  const checked = fieldset.querySelector('input:checked');
  // El tema volvió a pintar otra talla (p. ej. al reabrir la compra rápida): hay que elegir de nuevo.
  if (!(checked instanceof HTMLInputElement) || checked.dataset.optionValueId !== choice.valueId) {
    chosen.delete(picker);
    return true;
  }
  return false;
}

/** @param {Element | null | undefined} element @param {string} text */
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

/** Texto del botón (en la barra fija, sin tocar el contador de cantidad que va al lado). @param {Element} button */
const buttonLabel = (button) =>
  button.querySelector('.add-to-cart-text__content > span:first-child') || button.querySelector('.add-to-cart-text__content');

/** @param {Element} picker */
function applyPending(picker) {
  const fieldset = sizeFieldset(picker);
  if (!fieldset) return;

  for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (input.checked) input.checked = false;
    if (input.hasAttribute('checked')) input.removeAttribute('checked');
    if (input.dataset.currentChecked !== 'false') input.dataset.currentChecked = 'false';
    if (input.dataset.previousChecked !== 'false') input.dataset.previousChecked = 'false';
  }

  const productId = picker.getAttribute('data-product-id');
  const scope = scopeOf(picker);
  const form = scope.querySelector(`product-form-component[data-product-id="${productId}"]`);
  if (form) {
    for (const button of form.querySelectorAll('[ref="addToCartButton"]')) setText(buttonLabel(button), TEXT.choose);
    const accelerated = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (accelerated && !accelerated.hasAttribute('hidden')) accelerated.setAttribute('hidden', 'true');
  }

  const sticky = scope.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
  const stickyButton = sticky?.querySelector('[ref="addToCartButton"]');
  if (stickyButton) setText(buttonLabel(stickyButton), TEXT.choose);
  // La barra fija enseña la variante ("Tabaco/Blanco / 20"); sin talla elegida, solo el resto de opciones.
  const otherValues = [...picker.querySelectorAll('fieldset:not([data-kw-size-fieldset]) input:checked')].map(
    (input) => /** @type {HTMLInputElement} */ (input).value
  );
  setText(sticky?.querySelector('.sticky-add-to-cart__variant'), otherValues.join(' / '));
}

/** @param {Element} picker */
function showError(picker) {
  const fieldset = sizeFieldset(picker);
  if (!fieldset) return;
  fieldset.classList.add('kw-size--error');

  let message = fieldset.querySelector('.kw-size-error');
  if (!message) {
    message = document.createElement('p');
    message.className = 'kw-size-error';
    message.id = `kw-size-error-${++errorCount}`;
    message.setAttribute('role', 'alert');
    const legend = fieldset.querySelector('legend');
    if (legend) legend.after(message);
    else fieldset.prepend(message);
  }
  fieldset.setAttribute('aria-describedby', message.id);
  // Vaciar y reescribir para que el lector de pantalla lo anuncie en cada intento, no solo el primero.
  const alert = message;
  alert.textContent = '';
  setTimeout(() => (alert.textContent = TEXT.error), 50);

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  fieldset.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  const firstSize = fieldset.querySelector('input[type="radio"]:not([aria-disabled="true"])');
  if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });
}

/** Selector de talla que corresponde a un botón o formulario de compra. @param {Element} element */
function pickerFor(element) {
  const productId =
    element.closest('product-form-component')?.getAttribute('data-product-id') ||
    element.closest('sticky-add-to-cart')?.getAttribute('data-product-id');
  if (!productId) return null;
  return scopeOf(element).querySelector(`${PICKER}[data-product-id="${productId}"]`);
}

/** @param {Event} event */
const pickerOf = (event) => (event.target instanceof Element ? event.target.closest(PICKER) : null);

function scan() {
  for (const picker of document.querySelectorAll(PICKER)) {
    if (isPending(picker)) applyPending(picker);
  }
}

// Elección real del cliente (clic o teclado). Los "change" que dispara el propio tema no cuentan.
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!event.isTrusted || !(input instanceof HTMLInputElement)) return;
    const fieldset = input.closest('[data-kw-size-fieldset]');
    const picker = input.closest(PICKER);
    if (!fieldset || !picker) return;
    chosen.set(picker, {
      productId: picker.getAttribute('data-product-id') || '',
      valueId: input.dataset.optionValueId || '',
    });
    fieldset.classList.remove('kw-size--error');
    fieldset.removeAttribute('aria-describedby');
    fieldset.querySelector('.kw-size-error')?.remove();
  },
  true
);

document.addEventListener(ThemeEvents.variantSelected, (event) => {
  const picker = pickerOf(event);
  if (picker) inFlight.set(picker, performance.now());
});

// Llega la variante del servidor. Sin talla elegida (p. ej. cambió el color), dejar la talla otra vez sin marcar;
// con talla elegida, repetir la compra que se retuvo mientras el cambio estaba en vuelo.
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const picker = pickerOf(event);
  if (!picker) return;
  inFlight.delete(picker);
  const retained = deferred.get(picker);
  deferred.delete(picker);
  if (isPending(picker)) {
    applyPending(picker);
    return;
  }
  if (retained?.isConnected) {
    setTimeout(() => {
      if (retained instanceof HTMLFormElement) retained.requestSubmit();
      else if (retained instanceof HTMLElement) retained.click();
    });
  }
});

// Bloquear la compra mientras falte la talla: botón principal, barra fija y Enter dentro del formulario.
/** @param {Event} event */
function guard(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const trigger = event.type === 'submit' ? target : target.closest('[ref="addToCartButton"]');
  if (!trigger) return;
  const picker = pickerFor(trigger);
  if (!picker) return;

  if (isPending(picker)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    applyPending(picker);
    showError(picker);
    return;
  }

  // Fuera de la ficha (compra rápida) el tema añadiría la variante anterior si la nueva aún no llegó.
  const since = inFlight.get(picker);
  if (since !== undefined && performance.now() - since < IN_FLIGHT_MAX_MS) {
    event.preventDefault();
    event.stopImmediatePropagation();
    deferred.set(picker, trigger);
  }
}
// En window (fase de captura) para ir antes que los manejadores delegados del tema, que viven en document.
window.addEventListener('click', guard, true);
window.addEventListener('submit', guard, true);

scan();
// MutationObserver ya agrupa las mutaciones de cada tarea; scan() solo escribe si algo difiere, así que no hay bucle.
new MutationObserver(scan).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['data-current-checked', 'data-product-id', 'checked', 'hidden'],
});
window.kwBeltReady = true;
